import { useExamEventDetails } from '@exam-center/exam-event-management';
import {
  ExamEventCourseStatus,
  useGetParticipatedExamCoursesByExamEventIdQuery,
} from '@exam-center/graphql';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Divider, Flex, Form, FormSearchSelection } from '@shared/components';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useExamEventScoreReportSearchParams } from '../../hooks';

import {
  type ExamEventScoreReportFilterSectionFormInput,
  ExamEventScoreReportFilterSectionSchema,
  TAG_CATEGORY_OPTIONS,
} from './exam-event-score-report-filter-schema';

export default function ExamEventScoreReportFilterSection() {
  const {
    examEvent: { id: examEventId },
  } = useExamEventDetails();

  const [{ examCourseId, tagCategory }, { changeExamCourse, changeTagCategory, resetFilters }] =
    useExamEventScoreReportSearchParams();
  const [selectionKey, setSelectionKey] = useState(0);

  const { data, loading } = useGetParticipatedExamCoursesByExamEventIdQuery({
    variables: {
      input: {
        examEventId,
        statuses: [ExamEventCourseStatus.Allocated],
      },
    },
    skip: !examEventId,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });

  const examCourseOptions = useMemo(() => {
    const examEventCourses = data?.ecGetParticipatedExamCoursesByExamEventId.examCourses ?? [];

    return examEventCourses.map((examCourse) => ({
      label: examCourse.code,
      value: examCourse.id,
    }));
  }, [data]);

  const formContext = useForm<ExamEventScoreReportFilterSectionFormInput>({
    resolver: zodResolver(ExamEventScoreReportFilterSectionSchema),
    defaultValues: {
      examCourse: undefined,
      tagCategory: TAG_CATEGORY_OPTIONS.find((option) => option.value === tagCategory) ?? undefined,
    },
  });

  const handleSubmit = formContext.handleSubmit((data) => {
    changeExamCourse({
      examCourseId: data.examCourse?.value ?? '',
      examCourseLabel: data.examCourse?.label ?? '',
    });
    changeTagCategory(data.tagCategory?.value ?? null);
  });

  const handleReset = () => {
    formContext.reset({
      examCourse: undefined,
      tagCategory: undefined,
    });
    setSelectionKey((prev) => prev + 1);
    resetFilters();
  };

  useEffect(() => {
    if (loading) return;

    if (!examCourseId && !tagCategory) {
      formContext.reset({
        examCourse: undefined,
        tagCategory: undefined,
      });
      return;
    }

    formContext.reset({
      examCourse: examCourseOptions.find((option) => option.value === examCourseId) ?? undefined,
      tagCategory: TAG_CATEGORY_OPTIONS.find((option) => option.value === tagCategory) ?? undefined,
    });
  }, [examCourseId, examCourseOptions, formContext, loading, tagCategory]);

  return (
    <FormProvider {...formContext}>
      <Form
        onSubmit={handleSubmit}
        sx={(theme) => ({
          p: theme.spacing(4),
          borderRadius: theme.shape.rounded(2),
          border: '1px solid',
          borderColor: theme.palette.grey[300],
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(4),
          width: '100%',
        })}
      >
        <Flex
          sx={(theme) => ({
            gap: theme.spacing(4),
          })}
        >
          <FormSearchSelection
            key={`exam-course-${selectionKey}-${examCourseId}`}
            name='examCourse'
            label='หลักสูตร'
            options={examCourseOptions}
            loading={loading}
            placeholder='เลือกหลักสูตร'
            sx={{ flex: 1 }}
          />
          <FormSearchSelection
            key={`tag-category-${selectionKey}-${tagCategory ?? 'empty'}`}
            name='tagCategory'
            label='ประเภทคะแนน'
            options={TAG_CATEGORY_OPTIONS}
            placeholder='เลือกประเภทคะแนน'
            sx={{ flex: 1 }}
          />
        </Flex>

        <Divider />

        <Flex
          sx={(theme) => ({
            gap: theme.spacing(2),
            justifyContent: 'flex-end',
          })}
        >
          <Button color='secondary' variant='outlined' onClick={handleReset}>
            Reset
          </Button>
          <Button type='submit'>Apply</Button>
        </Flex>
      </Form>
    </FormProvider>
  );
}
